/**
 * Minimal client-side cache for expensive page data.
 *
 * Post-mortem 2026-08-13: Office Dashboard, Expense Reports, and
 * Reports were re-fetching everything on every navigation, making
 * tab-switches feel like cold starts even when the data was fresh
 * seconds ago.
 *
 * Design:
 * - Module-scoped `Map` so entries survive React unmounts but reset
 *   on full page reload (matches user expectation of "fresh after
 *   refresh").
 * - Simple TTL — an entry is considered fresh for TTL_MS after write.
 *   Stale entries are returned from `getStale` so pages can use the
 *   stale-while-revalidate pattern (render instantly, refetch in
 *   background, update when done).
 * - Namespace prefix invalidation so a mutation (e.g. approving an
 *   expense report) can wipe every cached list without knowing all
 *   the exact cache keys.
 */

const DEFAULT_TTL_MS = 60_000; // 60 seconds — long enough to help
                               // tab-switching, short enough that
                               // stale data isn't dangerous.

const cache = new Map(); // key → { ts: number, data: any }

export function getCached(key, ttlMs = DEFAULT_TTL_MS) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > ttlMs) {
    // Fresh window passed — treat as miss for callers that want
    // fresh data. Entry stays in map so `getStale` can still see it.
    return null;
  }
  return entry.data;
}

// Return the last cached value regardless of age. Used by the
// stale-while-revalidate pattern to paint the page instantly, then
// the caller kicks off a background refetch.
export function getStale(key) {
  const entry = cache.get(key);
  return entry ? entry.data : null;
}

export function setCached(key, data) {
  cache.set(key, { ts: Date.now(), data });
}

// Wipe entries whose key starts with `prefix`. Call this after any
// mutation that could make the cached list wrong.
export function invalidatePrefix(prefix) {
  for (const k of Array.from(cache.keys())) {
    if (k.startsWith(prefix)) cache.delete(k);
  }
}

export function clearAll() {
  cache.clear();
}
